import React, { useEffect, useState } from "react";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { portalApi } from "../lib/portalApi";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";

const REPORT_TYPES = [
  ["budget_audit",        "Budget Audit Report"],
  ["infrastructure_risk", "Infrastructure Risk Report"],
  ["citizen_impact",      "Citizen Impact Report"],
  ["cyber_readiness",     "Cybersecurity Readiness Report"],
  ["water_infra",         "Water Infrastructure Report"],
  ["executive_summary",   "Executive Summary"],
];

const Inner = () => {
  const { activeOrg } = usePortal();
  const [type, setType] = useState("executive_summary");
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => portalApi.listReports().then(r => setReports(r.reports || []));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!activeOrg) return toast.error("Select an organization in the header");
    setBusy(true);
    try {
      const r = await portalApi.generateReport({ organization_id: activeOrg, report_type: type });
      toast.success(`Generated: ${r.title}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Report generation failed"); }
    setBusy(false);
  };

  const downloadReport = async (id, title, format) => {
    try {
      const data = await portalApi.downloadReport(id, format);
      const isText = format === "csv" || format === "md";
      const content = isText ? data : JSON.stringify(data, null, 2);
      const mime = format === "csv" ? "text/csv" :
                   format === "md"  ? "text/markdown" : "application/json";
      const blob = new Blob([content], { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${title.replace(/\s+/g, "-").toLowerCase()}-${id.slice(0, 8)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Reports</div>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Generate & Download</h1>
      </div>

      <div className="glass p-6 grid md:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Report type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1 bg-navy-deep border-gold-dim/40 text-cream" data-testid="report-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {REPORT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button disabled={busy} onClick={generate} className="btn-gold h-11 px-6" data-testid="report-generate">
          {busy ? "Generating…" : "Generate Report"}
        </Button>
      </div>

      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="reports-table">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
            <tr className="border-b border-gold-dim/20">
              <th className="text-left p-4">Created</th>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Organization</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-steel">No reports yet. Generate one above.</td></tr>
            ) : reports.map(r => (
              <tr key={r.id} className="border-b border-gold-dim/10">
                <td className="p-4 text-cream-dim">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-4 text-cream flex items-center gap-2"><FileText size={14} className="text-gold" /> {r.title}</td>
                <td className="p-4 text-cream-dim">{r.organization_name}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => downloadReport(r.id, r.title, "json")} className="text-xs text-electric hover:text-gold"
                    data-testid={`dl-json-${r.id}`}><Download size={12} className="inline mr-1" />JSON</button>
                  <button onClick={() => downloadReport(r.id, r.title, "csv")}  className="text-xs text-electric hover:text-gold"
                    data-testid={`dl-csv-${r.id}`}><Download size={12} className="inline mr-1" />CSV</button>
                  <button onClick={() => downloadReport(r.id, r.title, "md")}   className="text-xs text-electric hover:text-gold"
                    data-testid={`dl-md-${r.id}`}><Download size={12} className="inline mr-1" />MD</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PortalReports = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalReports;
